/**
 * Shared types for API responses
 */

export interface JwtPayload {
  sub: string; // user ID
  email: string;
  roles: string[];
  /** Global role + direct grants only. */
  permissions: string[];
  org_id?: string; // active organization ID
  org_role?: string; // active org role slug
  /** Active org system template key — expands org catalog without bloating the JWT. */
  org_system_key?: "admin" | "member";
  /** Permissions from the active org role only (omitted for admin system key). */
  org_permissions?: string[];
  /** Compact membership index across orgs (may be omitted for multi-org users). */
  orgs?: Array<{
    id: string;
    roleId: string;
    role: string;
    systemKey?: "admin" | "member";
  }>;
  masqueraded_by?: string;
  /** Session id — identifies which refresh-token row minted this access token (see user-service). */
  sid?: string;
  /** Token-version snapshot at mint time (see user-service auth-revocation). */
  tv?: number;
  iat: number;
  exp: number;
}

export interface TokenDto {
  access_token: string;
  refresh_token: string;
  refresh_token_expires_at: string;
}

export interface GoogleOAuthTokenDto extends TokenDto {
  is_new_user?: boolean;
}

/** Returned instead of tokens when login/register finds an unlinked Smart Link claim. */
export interface SmartLinkOnboardingResponse {
  needsSmartLinkOnboarding: true;
  ticket: string;
  preview: {
    email: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
  };
}

export function isSmartLinkOnboardingResponse(
  value: TokenDto | SmartLinkOnboardingResponse,
): value is SmartLinkOnboardingResponse {
  return (value as SmartLinkOnboardingResponse).needsSmartLinkOnboarding === true;
}

/**
 * Returned instead of creating a new account when registration targets an
 * email with an unlinked Smart claim — passwords must stay synced between
 * the two systems, so the user logs in with their existing Lion Smart
 * password instead (that path verifies it and offers onboarding).
 */
export interface SmartLinkAccountExistsResponse {
  smartLinkAccountExists: true;
  email: string;
}

export function isSmartLinkAccountExistsResponse(
  value: TokenDto | SmartLinkAccountExistsResponse,
): value is SmartLinkAccountExistsResponse {
  return (value as SmartLinkAccountExistsResponse).smartLinkAccountExists === true;
}
